import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpDeleteRequest } from "@agtech/core/data/http/AgTechApiDeleteRequests";
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { useAgTechApiPutRequest } from "@agtech/core/data/http/AgTechApiPutRequests";
import { CreateOperationAddressDTO, OperationAddress, UpdateOperationAddressDTO, ProductDTO, CreateLicenseDTO, CheckoutSessionRedirectUrl, ProvisionTrialDTO } from "./OperationData";
import { OperationDataGetApplicationUserRolesQuery, OperationDataGetAvailableApplicationsQuery, OperationDataGetOperationAddressesQuery, OperationDataGetOperationLicenseHistoryQuery, OperationDataGetOperationLicensedApplicationsQuery, OperationDataGetOperationLogoQuery } from "./OperationDataQueries";
import { AgTechIdentityBaseURL } from "@agtech/core/auth/AgTechAuthData";

export type ApplicationUserRoleActionDTO = {
    userId: number,
    roleId: number
}

export const useOperationDataAssignApplicationRoleAction = (appId: string): AgTechDataAction<ApplicationUserRoleActionDTO, boolean> => {
    let assignRequest = useAgTechHttpPostRequest<{ userId: number, roles: number[] }, boolean>();

    return {
        name: 'AssignApplicationRole',
        getConfiguration: () => ({
            actionExecutionMessage: 'Assigning role...',
            actionConfirmationMessage: 'Successfully assigned role',
            showConfirmationMessage: false
        }),
        action: props => assignRequest.post({
            baseURL: AgTechIdentityBaseURL,
            path: `Operations/Applications/${appId}/Users`,
            postedData: { userId: props.submittedEntity.userId, roles: [props.submittedEntity.roleId] }
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetApplicationUserRolesQuery,
                queryContext: { appId }
            });
        }
    }
}

export const useOperationDataRemoveApplicationRoleAction = (appId: string): AgTechDataAction<ApplicationUserRoleActionDTO, boolean> => {
    let removeRequest = useAgTechHttpDeleteRequest();

    return {
        name: 'RemoveApplicationRole',
        getConfiguration: () => ({
            actionExecutionMessage: 'Removing role...',
            actionConfirmationMessage: 'Successfully removed role',
            showConfirmationMessage: false
        }),
        action: props => removeRequest.delete({
            baseURL: AgTechIdentityBaseURL,
            path: `Operations/Applications/${appId}/Users/${props.submittedEntity.userId}/Roles/${props.submittedEntity.roleId}`
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetApplicationUserRolesQuery,
                queryContext: { appId }
            });
        }
    }
}

export const useOperationDataSetOperationLogoAction = (): AgTechDataAction<File, any> => {
    let setLogoRequest = useAgTechHttpPostRequest<FormData, string>();

    return {
        name: 'SetOperationLogo',
        getConfiguration: () => ({
            actionExecutionMessage: 'Uploading your logo...'
        }),
        validate: async props => {
            props.validation.failWithWarningIf(
                !props.submittedEntity.type.startsWith('image/'),
                'The file you selected is not an image. Please select an image file.'
            );

            props.validation.failWithWarningIf(
                props.submittedEntity.size > 10000000,
                'The file you selected is too large. Please select a file smaller than 10 MB.'
            );
        },
        action: async props => {
            const formData = new FormData();
            formData.append('file', props.submittedEntity);

            return await setLogoRequest.post({
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations/Logo',
                postedData: formData
            });
        },
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetOperationLogoQuery
            });
        }
    }
}

export const useOperationDataWriteOperationAddress = (): AgTechDataAction<CreateOperationAddressDTO, OperationAddress> => {
    let writeUserSubmissionRequest = useAgTechHttpPostRequest<CreateOperationAddressDTO, OperationAddress>();

    return {
        name: 'CreateOperationAddress',
        getConfiguration: () => ({
            actionExecutionMessage: 'Creating Address...',
            actionConfirmationMessage: 'Successfully created address'
        }),
        action: async props => writeUserSubmissionRequest.post({
            path: 'Operations/Address',
            postedData: props.submittedEntity
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetOperationAddressesQuery
            });
        }
    }
}

export const useOperationDataDeleteOperationAddressAction = (): AgTechDataAction<number, any> => {
    let operationAddressDeleteRequest = useAgTechHttpDeleteRequest();

    return {
        name: 'DeleteAddress',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting address...',
            actionConfirmationMessage: 'Successfully deleted address'
        }),
        action: props => operationAddressDeleteRequest.delete({
            path: 'Operations/Address/' + props.submittedEntity
        })
    }
}

export const useOperationDataUpdateAddressAction = (): AgTechDataAction<UpdateOperationAddressDTO, any> => {
    let operationAddressDeleteRequest = useAgTechApiPutRequest();

    return {
        name: 'UpdateAddress',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating address...',
            actionConfirmationMessage: 'Successfully updated address'
        }),
        action: props => operationAddressDeleteRequest.put({
            path: 'Operations/Address/' + props.submittedEntity.id,
            postedData: props.submittedEntity
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetOperationAddressesQuery
            });
        }
    }
}

export const useOperationDataGetLicenseAction = (): AgTechDataAction<ProductDTO, any> => {
    let getLicenseRequest = useAgTechHttpPostRequest();

    return {
        name: 'GetLicense',
        getConfiguration: () => ({
            actionExecutionMessage: 'Getting License...',
            actionConfirmationMessage: 'Successfully got license'
        }),
        action: props => getLicenseRequest.post({
            path: 'Operations/Licenses',
            postedData: props.submittedEntity
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetOperationLicensedApplicationsQuery
            });
        }
    }
}

export const useOperationDataGetCheckoutSessionAction = (): AgTechDataAction<CreateLicenseDTO, CheckoutSessionRedirectUrl> => {
    let getcheckoutSessionRequest = useAgTechHttpPostRequest<CreateLicenseDTO, CheckoutSessionRedirectUrl>();

    return {
        name: 'GetCheckoutSession',
        getConfiguration: () => ({
            actionExecutionMessage: 'Getting a checkout session...',
            actionConfirmationMessage: 'Successfully got a checkout session'
        }),
        action: props => getcheckoutSessionRequest.post({
            path: 'Operations/Licenses/CheckoutSession',
            postedData: props.submittedEntity
        }),
        onSuccess: async props => {
            // TODO: What to refresh here

            window.location.href = props.responseData.redirectUrl;
        }
    }
}

export const useOperationDataProvisionApplicationTrialAction = (): AgTechDataAction<ProvisionTrialDTO, boolean> => {
    let provisionTrialRequest = useAgTechHttpPostRequest<ProvisionTrialDTO, boolean>();

    return {
        name: 'ProvisionApplicationTrial',
        getConfiguration: () => ({
            actionExecutionMessage: 'Starting your trial...',
            actionConfirmationMessage: 'Your trial has started'
        }),
        action: props => provisionTrialRequest.post({
            path: 'Operations/Applications/ProvisionTrial',
            postedData: props.submittedEntity
        }),
        onSuccess: async props => {
            props.invalidateQuery({
                query: OperationDataGetOperationLicensedApplicationsQuery
            });
            props.invalidateQuery({
                query: OperationDataGetOperationLicenseHistoryQuery
            });
            props.invalidateQuery({
                query: OperationDataGetAvailableApplicationsQuery
            });
        }
    }
}